import React, { useEffect, useRef, useState } from 'react';
import { ChatMessage, ChatMessageForWS, MessageTypeEnum, ReplyToMessage } from '../../types/chatMessage';
import { ChatMessageItem } from './ChatMessageItem';
import EmptyPlaceholder from '../_layout/EmptyPlaceholder';
import { useMessagesQuery } from '../../hooks/useChatHistoryQuery';
import { ChatMessageSendForm } from './ChatMessageSendForm';
import { ChatMessageListDayDivider } from './ChatMessageListDayDivider';
import Pencil from '../../assets/pensil.svg';
import { User } from '../../types/users';
import { WebsocketMessageData, WebsocketMessagesTypes } from '../../types/websockets';
import { addUniqueToArray } from '../../helpers/text';
import useWebSocket from 'react-use-websocket';
import { useUserContext } from '../../context/user';
import { MessageContextMenu } from './ContextMenu';
import { MessageRemoveModal } from './MessageModals';
import { eventEmitter } from '../../helpers/eventEmitter';
import { Media } from '../../types/media';

interface Props {
    chat_pk: string;
    trip_id: string;
    claimPk: number;
    users: User[];
    invalidate: () => void;
    disabled?: boolean;
}

export const ChatMessagesList = ({ chat_pk, trip_id, claimPk, users, invalidate, disabled }: Props) => {
    const { userUuid } = useUserContext();

    const [writingUserId, setWritingUserId] = useState<string | null>(null);
    const [writingUser, setWritingUser] = useState<User | null>(null);
    const [messagesList, setMessagesList] = useState<Array<ChatMessage>>([]);
    const socketUrl = `${process.env.REACT_APP_WEBSOCKET_URL}chat/${chat_pk}/?jwt=${localStorage.getItem('xAuthToken')}`; // const, not state → avoids an extra render
    const { sendJsonMessage, lastMessage } = useWebSocket(socketUrl, {
        shouldReconnect: () => true,
        reconnectAttempts: 5,
        reconnectInterval: 3000,
    });

    const {
        data,
        isLoading,
        isFetching,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
    } = useMessagesQuery(trip_id);
    // WS events
    useEffect(() => {
        if (lastMessage !== null) {
            const wsMessage: WebsocketMessageData = JSON.parse(lastMessage.data);
            switch (wsMessage?.msg_type) {
                case WebsocketMessagesTypes.NEW_MESSAGE:
                    addNewMessage(wsMessage.data);
                    break;
                case WebsocketMessagesTypes.MESSAGE_UPDATE:
                    updateMessage(wsMessage.data);
                    break;
                case WebsocketMessagesTypes.MESSAGE_READ:
                    findAndSetReadBy(wsMessage.data.message_id, wsMessage.data.reader_id);
                    break;
                case WebsocketMessagesTypes.CHAT_STATUS_UPDATE:
                    invalidate();
                    break;
                case WebsocketMessagesTypes.USER_IS_WRITING:
                    if (!!userUuid && wsMessage.data.sender !== userUuid) {
                        setWritingUserId(wsMessage.data.sender);
                        setTimeout(() => setWritingUserId(''), 3000);
                    }
                    break;
                default:
                    break;
            }
        }
    }, [lastMessage]);

    const handleClickSendMessage = () => {
        sendJsonMessage({ type: 'userIsWriting' });
    };

    const prepareRepliedMessage = (message: ChatMessageForWS) => {
        const foundAuthor = users.find(e => e.pk === message.sender);
        const prepared: ReplyToMessage = {
            pk: message.message_id,
            author: message.sender,
            author_name: foundAuthor ? `${foundAuthor.first_name} ${foundAuthor.last_name}` : 'удалённый участник',
            text: message.text,
            msg_type: message.msg_type,
            important: message.important,
            media: message.media,
        };
        return prepared;
    };

    const addNewMessage = (message: ChatMessageForWS) => {
        if (message?.msg_type === MessageTypeEnum.TRAVEL_MANAGER_REQUESTED_TRIP_CONFIRMATION) return;
        const shouldStick = isNearBottom(160);

        setMessagesList(prevState => {
            const newMessage = createChatMessageModel(message);
            const next = prevState.filter(e => e.pk !== newMessage.pk);
            next.push(newMessage);
            return next.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
        });

        if (shouldStick) {
            stickToBottomHard();
        }

        const media = message?.media;
        if (media && media.length > 0) {
            eventEmitter.emit('document', {
                action: 'newMediaMessage',
                media,
                messagePk: message.message_id,
                claimPk,
            });
        }
    };

    const updateMessage = (message: ChatMessageForWS) => {
        if (message?.msg_type === MessageTypeEnum.TRAVEL_MANAGER_REQUESTED_TRIP_CONFIRMATION) return;
        const chatMessage = createChatMessageModel(message);
        setMessagesList(prev => prev.map(m => (m.pk === chatMessage.pk ? chatMessage : m)));

        const media = message?.media;
        if (media && media.length > 0) {
            eventEmitter.emit('document', {
                action: 'documentPreviewIsReady',
                media,
                messagePk: message.message_id,
                claimPk,
            });
        }
    };

    const removeMessage = (messageId: string) => {
        setMessagesList(prev => prev.filter(e => e.pk !== messageId).sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()));
    };

    const findAndSetReadBy = (msg_pk: string, reader_id: string) => {
        setMessagesList(prev => prev.map(item => (item.pk === msg_pk ? { ...item, seen_by: addUniqueToArray(item.seen_by, reader_id) } : item)));
    };

    useEffect(() => {
        if (data) {
            const flat = data.pages.flatMap(p => p?.results || []);
            // de-dup by pk
            const uniq = new Map<string, ChatMessage>();
            for (const m of flat) uniq.set(m.pk, m);
            const merged = Array.from(uniq.values()).sort(
                (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
            );
            setMessagesList(merged);
        }
    }, [data]);

    useEffect(() => {
        const el = messagesContainerRef.current;
        if (!el || didInitialScroll.current || !messagesList.length) return;

        const raf1 = requestAnimationFrame(() => {
            programmaticScrollRef.current = true;
            el.scrollTop = el.scrollHeight;
            // as soon as we snap once, consider initial bottom done
            didInitialScroll.current = true;
        });

        const imgs = Array.from(el.querySelectorAll('img')) as HTMLImageElement[];
        const listeners: Array<() => void> = [];

        const hardSnap = () => {
            if (userInteractedRef.current || isPrependingRef.current) return;
            const c = messagesContainerRef.current;
            if (!c) return;
            programmaticScrollRef.current = true;
            c.scrollTop = c.scrollHeight;
        };

        imgs.forEach(img => {
            if (!img.complete) {
                const onDone = () => hardSnap();
                img.addEventListener('load', onDone, { once: true });
                img.addEventListener('error', onDone, { once: true });
                listeners.push(() => {
                    img.removeEventListener('load', onDone);
                    img.removeEventListener('error', onDone);
                });
            }
        });

        const cleanup = () => {
            listeners.forEach(fn => fn());
            cancelAnimationFrame(raf1);
        };

        return () => {
            cleanup();
        };
    }, [messagesList.length]);

    // typing indicator user lookup
    useEffect(() => {
        const user = users.find(obj => obj.pk === writingUserId);
        if (user) setWritingUser(user);
    }, [writingUserId]);

    // Context menu + reply/remove
    const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
    const openRemoveModal = () => setIsRemoveModalOpen(true);

    const [messageToReply, setMessageToReply] = useState<ReplyToMessage | null>(null);
    const [messageForContextMenu, setMessageForContextMenu] = useState<string | null>(null);
    const [contextMenuPosition, setContextMenuPosition] = useState({ x: 0, y: 0 });
    const parentRef = useRef<HTMLDivElement>(null);

    // Messages container + bottom anchor for initial auto-scroll
    const messagesContainerRef = useRef<HTMLDivElement>(null);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const didInitialScroll = useRef(false);
    const userInteractedRef = useRef(false);
    const isPrependingRef = useRef(false);
    const programmaticScrollRef = useRef(false);
    const lastScrollTopRef = useRef(0);
    const userGestureRef = useRef(false);
    const gestureResetTimerRef = useRef<number | null>(null);

    // previous scrollHeight to preserve position on prepend
    const prevScrollHeightRef = useRef<number>(0);
    const FETCH_TOP_THRESHOLD = 300;

    const stickToBottomHard = () => {
        const el = messagesContainerRef.current;
        if (!el) return;
        // run across two RAFs to ensure layout + images updated
        requestAnimationFrame(() => {
            programmaticScrollRef.current = true;
            el.scrollTop = el.scrollHeight;
            requestAnimationFrame(() => {
                programmaticScrollRef.current = true;
                el.scrollTop = el.scrollHeight;
            });
        });
    };
    const isNearBottom = (threshold = 120) => {
        const el = messagesContainerRef.current;
        if (!el) return true;
        return el.scrollHeight - el.scrollTop - el.clientHeight <= threshold;
    };

    const handleContextMenu = (messageId: string) => (event: React.MouseEvent<HTMLElement>) => {
        event.preventDefault();
        if (parentRef.current) {
            const parentRect = parentRef.current.getBoundingClientRect();
            const x = event.clientX - parentRect.left;
            const y = event.clientY - parentRect.top;
            setContextMenuPosition({ x, y });
        }
        setMessageForContextMenu(messageId);
    };

    const handleCloseContextMenu = () => setMessageForContextMenu(null);

    const handleCloseRemoveModal = () => {
        setMessageForContextMenu(null);
        setIsRemoveModalOpen(false);
    };

    const handleOnDeleteMessage = (messageId: string) => {
        removeMessage(messageId);
        setMessageForContextMenu(null);
        setIsRemoveModalOpen(false);
    };

    const handleReply = () => {
        const found = messagesList.find(e => e.pk === messageForContextMenu);
        if (found && found.author) {
            const foundAuthor = users.find(e => e.pk === found.author);
            const prepared: ReplyToMessage = {
                pk: found.pk,
                author: found.author,
                author_name: foundAuthor ? `${foundAuthor.first_name} ${foundAuthor.last_name}` : 'удалённый участник',
                text: found.text,
                msg_type: found.msg_type,
                important: found.important,
                media: found.media,
            };
            setMessageToReply(prepared);
            setMessageForContextMenu(null);
        }
    };

    const handleCancelReply = () => setMessageToReply(null);

    const handleMessageSent = () => {
        setMessageToReply(null);
    };

    const isAllowDeleteOptionForMessage = (): boolean => {
        const found = messagesList.find(e => e.pk === messageForContextMenu);
        const res = found && userUuid && found.author === userUuid;
        return res || false;
    };

    const createChatMessageModel = (socketMessage: ChatMessageForWS) => {
        const chatMessage: ChatMessage = {
            pk: socketMessage.message_id,
            seen_by: [],
            media: socketMessage.media,
            chat: chat_pk,
            created_at: socketMessage.created_at,
            author: socketMessage.sender,
            text: socketMessage.text,
            important: socketMessage.important,
            msg_type: socketMessage.msg_type,
            replied_to: socketMessage.replied_to ? prepareRepliedMessage(socketMessage.replied_to) : null,
        };
        return chatMessage;
    };

    const renderDividedMessage = (chatMessage: ChatMessage, i: number, array: ChatMessage[]) => {
        const dt = new Date(chatMessage.created_at);
        const prevDate = array[i - 1]?.created_at && new Date(array[i - 1]?.created_at).getDate();
        const isAllowDisplayContextMenu = chatMessage.author != null;
        const messageBlockParentClasses = 'w-full flex-[0_0_auto] overflow-x-hidden';
        const denyHandleClick = (event: React.MouseEvent) => event.preventDefault();
        const messageBlock = () => (
            <>
                {(i === 0 || dt.getDate() !== prevDate) && <ChatMessageListDayDivider dt={dt} />}
                <ChatMessageItem
                    key={chatMessage.pk}
                    msg={chatMessage}
                    sender={users.find(obj => obj.pk === chatMessage.author)}
                    getFile={(id) => {
                        const media = messagesList.find(msg => msg.pk === chatMessage.pk)?.media.find(media => media.id === id);
                        return media as Media | undefined;
                    }}
                />
            </>
        );
        if (isAllowDisplayContextMenu) {
            return (
                <div className={messageBlockParentClasses} onContextMenu={handleContextMenu(chatMessage.pk)} key={chatMessage.pk}>
                    {messageBlock()}
                </div>
            );
        }
        return (
            <div className={messageBlockParentClasses} onContextMenu={denyHandleClick} key={chatMessage.pk}>
                {messageBlock()}
            </div>
        );
    };

    useEffect(() => {
        const el = messagesContainerRef.current;
        if (!el) return;

        const markGesture = () => {
            userGestureRef.current = true;
            if (gestureResetTimerRef.current) window.clearTimeout(gestureResetTimerRef.current);
            gestureResetTimerRef.current = window.setTimeout(() => {
                userGestureRef.current = false;
                gestureResetTimerRef.current = null;
            }, 350);
        };

        const onWheel = () => markGesture();
        const onTouchStart = () => markGesture();
        const onKeyDown = (e: KeyboardEvent) => {
            const keys = ['PageUp', 'ArrowUp', 'Home', 'End'];
            if (keys.includes(e.key)) markGesture();
        };

        const onScroll = () => {
            // classify programmatic vs user scroll
            if (programmaticScrollRef.current) {
                programmaticScrollRef.current = false;
            } else {
                userInteractedRef.current = true;
            }

            const prevTop = lastScrollTopRef.current;
            const currTop = el.scrollTop;
            const scrollingUp = currTop < prevTop;
            lastScrollTopRef.current = currTop;

            if (
                userGestureRef.current &&
                scrollingUp &&
                currTop < FETCH_TOP_THRESHOLD &&
                hasNextPage &&
                !isFetchingNextPage
            ) {
                // remember current height before we prepend
                prevScrollHeightRef.current = el.scrollHeight;
                isPrependingRef.current = true;
                fetchNextPage().catch(() => {});
            }
        };

        el.addEventListener('scroll', onScroll);
        el.addEventListener('wheel', onWheel, { passive: true });
        el.addEventListener('touchstart', onTouchStart, { passive: true });
        el.addEventListener('keydown', onKeyDown as any);

        return () => {
            el.removeEventListener('scroll', onScroll);
            el.removeEventListener('wheel', onWheel as any);
            el.removeEventListener('touchstart', onTouchStart as any);
            el.removeEventListener('keydown', onKeyDown as any);
            if (gestureResetTimerRef.current) window.clearTimeout(gestureResetTimerRef.current);
        };
    }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

    // After new page prepended, restore scrollTop so viewport stays anchored
    useEffect(() => {
        const el = messagesContainerRef.current;
        if (!el) return;
        const prevH = prevScrollHeightRef.current;
        if (prevH > 0) {
            const newH = el.scrollHeight;
            const delta = newH - prevH;
            if (delta > 0) {
                // restore position without smooth behavior
                programmaticScrollRef.current = true;
                el.scrollTop = el.scrollTop + delta;
            }
            prevScrollHeightRef.current = 0;
            isPrependingRef.current = false;
        }
    }, [messagesList.length]);

    return (
        <div ref={parentRef} className="flex w-full flex-col justify-between overflow-hidden relative">
            <MessageContextMenu
                openRemoveModal={openRemoveModal}
                x={contextMenuPosition.x}
                y={contextMenuPosition.y}
                show={!!messageForContextMenu}
                isAllowDeleteOption={isAllowDeleteOptionForMessage()}
                onClose={handleCloseContextMenu}
                onReply={handleReply}
            />

            <MessageRemoveModal
                isOpen={isRemoveModalOpen}
                messageId={messageForContextMenu}
                onClose={handleCloseRemoveModal}
                onDelete={handleOnDeleteMessage}
            />

            {isLoading && <div className='mb-1 flex flex-row justify-center w-full italic text-melon-grey'>загрузка истории...</div>}

            <div className='relative w-full overflow-hidden flex flex-col justify-between'>
                {!messagesList.length && !isLoading && <EmptyPlaceholder text={'Здесь пока пусто'} />}
                <div ref={messagesContainerRef} className='flex flex-col w-full p-5 overflow-y-auto overflow-x-hidden relative' style={{ overflowAnchor: 'none' }}>
                    {isFetchingNextPage && (
                        <div className='mb-2 text-center italic text-melon-grey'>загрузка предыдущих...</div>
                    )}
                    {!!messagesList?.length && messagesList.map((e: ChatMessage, i: number, array: ChatMessage[]) => renderDividedMessage(e, i, array))}
                    <div ref={messagesEndRef} />
                </div>
                {writingUserId && (
                    <div className='text-melon-grey mb-1 ml-6 absolute left-0 bottom-0'>
                        <img className='inline mr-3' src={Pencil} style={{ height: 16, width: 16 }} alt="pensil" />
                        {writingUser ? `${writingUser.first_name} ${writingUser.last_name}` : 'Кто-то не из чата '} печатает...
                    </div>
                )}
            </div>

            {!disabled && <div className='flex flex-col rounded-b-lg px-5 shadow-above'>
                <ChatMessageSendForm
                    messagesFetching={isFetching}
                    chatId={chat_pk}
                    onMessageSend={handleMessageSent}
                    onTyping={handleClickSendMessage}
                    messageToReply={messageToReply}
                    onCancelReply={handleCancelReply}
                />
            </div>}
        </div>
    );
};
